<script lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import moveIconUrl from '@sg/resources/assets/icons/move.svg';
import { PhxsgIcon, type PhxsgIconIds } from '@sg/src/View/Components/Atoms/Icon';
import { PhxsgLoader } from '@sg/src/View/Components/Atoms/Loader';

export type ProductAnimationMode = 'auto' | 'manual';
export type ProductAnimationFit = 'contain' | 'cover';

export interface ProductAnimationI18n {
  manual?: string;
}

export interface ProductAnimationIcon {
  id: PhxsgIconIds;
  title?: string;
}

export interface PhxsgProductAnimationProps {
  sources: string[];
  mode?: ProductAnimationMode;
  fit?: ProductAnimationFit;
  backgroundColor?: string | null;
  sourceCount?: number;
  i18n?: ProductAnimationI18n;
  iconManual?: ProductAnimationIcon | null;
}

const DEFAULT_MANUAL_ICON: ProductAnimationIcon = {
  id: 'move',
};
</script>

<script setup lang="ts">
const FRAME_DURATION = 30;

const props = withDefaults(defineProps<PhxsgProductAnimationProps>(), {
  mode: 'auto',
  fit: 'contain',
  backgroundColor: null,
  sourceCount: undefined,
  i18n: () => ({}),
  iconManual: () => DEFAULT_MANUAL_ICON,
});

const rootRef = ref<HTMLElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const frames = ref<HTMLImageElement[]>([]);
const currentIndex = ref(0);
const isLoading = ref(true);

let animationInterval: ReturnType<typeof window.setInterval> | null = null;
let loadRequestId = 0;
let ignoreClickUntil = 0;

const classes = computed(() => [
  'phxsg-product-animation',
  `phxsg-product-animation--${props.fit}`,
  `phxsg-product-animation--${props.mode}`,
]);

const manualIconId = computed(() => props.iconManual?.id ?? DEFAULT_MANUAL_ICON.id);
const usesMoveIconAsset = computed(() => manualIconId.value === DEFAULT_MANUAL_ICON.id);
const manualIconTitle = computed(() => props.iconManual?.title ?? props.i18n.manual ?? 'Move');
const resolvedSourceCount = computed(() => props.sourceCount ?? props.sources.length);
const showManualIcon = computed(
  () => props.mode === 'manual' && props.iconManual !== null && !isLoading.value && resolvedSourceCount.value > 1,
);
const rootStyle = computed(() => ({
  backgroundColor: props.backgroundColor ?? undefined,
}));

function stopAnimation(): void {
  if (animationInterval) {
    window.clearInterval(animationInterval);
    animationInterval = null;
  }
}

function drawCurrentFrame(): void {
  const canvas = canvasRef.value;
  const context = canvas?.getContext('2d');
  const frame = frames.value[currentIndex.value];

  if (!canvas || !context || !frame) {
    return;
  }

  canvas.width = frame.naturalWidth;
  canvas.height = frame.naturalHeight;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(frame, 0, 0, canvas.width, canvas.height);
}

function syncAnimation(): void {
  stopAnimation();

  if (props.mode !== 'auto' || frames.value.length <= 1) {
    return;
  }

  animationInterval = window.setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % frames.value.length;
  }, FRAME_DURATION);
}

function preloadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load animation frame: ${source}`));
    image.src = source;
  });
}

function isFulfilled<T>(result: PromiseSettledResult<T>): result is PromiseFulfilledResult<T> {
  return result.status === 'fulfilled';
}

async function loadFrames(): Promise<void> {
  loadRequestId += 1;
  const requestId = loadRequestId;

  stopAnimation();
  isLoading.value = true;
  currentIndex.value = 0;
  frames.value = [];

  if (!props.sources.length) {
    isLoading.value = false;
    return;
  }

  const results = await Promise.allSettled(props.sources.map((source) => preloadImage(source)));

  if (requestId !== loadRequestId) {
    return;
  }

  frames.value = results.filter(isFulfilled).map((result) => result.value);
  isLoading.value = false;
  drawCurrentFrame();
  syncAnimation();
}

function setFrameFromClientX(clientX: number): void {
  const element = rootRef.value;

  if (!element || frames.value.length <= 1) {
    return;
  }

  const rect = element.getBoundingClientRect();

  if (!rect.width) {
    return;
  }

  const progress = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 0.999999);
  currentIndex.value = Math.floor(progress * frames.value.length);
}

function handleMouseMove(event: MouseEvent): void {
  if (props.mode !== 'manual') {
    return;
  }

  setFrameFromClientX(event.clientX);
}

function handleTouchMove(event: TouchEvent): void {
  if (props.mode !== 'manual') {
    return;
  }

  const [touch] = event.touches;

  if (!touch) {
    return;
  }

  ignoreClickUntil = Date.now() + 400;
  event.preventDefault();
  event.stopPropagation();
  setFrameFromClientX(touch.clientX);
}

function handleClickCapture(event: MouseEvent): void {
  if (props.mode !== 'manual' || Date.now() >= ignoreClickUntil) {
    return;
  }

  ignoreClickUntil = 0;
  event.preventDefault();
  event.stopPropagation();
}

watch(
  () => props.sources,
  () => {
    void loadFrames();
  },
  { immediate: true, deep: true },
);

watch(currentIndex, () => {
  drawCurrentFrame();
});

watch(
  () => props.mode,
  () => {
    syncAnimation();
  },
);

onBeforeUnmount(() => {
  stopAnimation();
});
</script>

<template>
  <div
    ref="rootRef"
    :class="classes"
    :style="rootStyle"
    :data-mode="mode"
    :data-sources="resolvedSourceCount"
    @click.capture="handleClickCapture"
    @mousemove="handleMouseMove"
    @touchmove="handleTouchMove"
  >
    <div v-if="isLoading" class="phxsg-product-animation__loader">
      <PhxsgLoader />
    </div>

    <canvas v-show="!isLoading" ref="canvasRef" class="phxsg-product-animation__canvas" />

    <div v-if="showManualIcon" class="phxsg-product-animation__icon-wrapper" aria-hidden="true">
      <span class="phxsg-product-animation__icon-badge">
        <img v-if="usesMoveIconAsset" :src="moveIconUrl" alt="" class="phxsg-product-animation__icon-image" />
        <PhxsgIcon
          v-else
          :id="manualIconId"
          size="md"
          :title="manualIconTitle"
          class="phxsg-product-animation__icon-svg"
        />
      </span>
    </div>
  </div>
</template>

<style src="@sg-core/src/View/Components/Atoms/Loader/loader.css" />
<style src="@sg/src/View/Components/Atoms/Loader/loader.css" />
<style src="./product-animation.css" />
